import { useScrollReveal } from '../hooks/useScrollReveal';

const fiveValues = [
  { keyword: '专业判断', desc: '政治方向、理论阐释、价值引导和育人效果，概念是否讲准、案例是否支撑理论', icon: 'ri-scales-3-line', color: 'accent' },
  { keyword: '现场经验', desc: '结构是否稳、导入是否有效、案例是否堆砌、学生回应是否进入课堂推进', icon: 'ri-eye-line', color: 'primary' },
  { keyword: '学科理解', desc: '共同富裕与人民立场、发展质量、制度优势之间的关系把握', icon: 'ri-stack-line', color: 'primary' },
  { keyword: '育人把握', desc: '知识、理论、价值和学生成长是否真正联系起来', icon: 'ri-user-heart-line', color: 'primary' },
  { keyword: '教学直觉', desc: '长期积累的判断力，能快速识别课堂的质量信号和潜在问题', icon: 'ri-flashlight-line', color: 'accent' },
];

const sceneTags = ['说课', '评课', '互动诊断', '听评课', '专家讨论'];

export default function P11TraditionalValue({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      {/* 背景光晕 */}
      <div className="absolute top-10 right-16 w-72 h-72 rounded-full bg-primary-100/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-10 left-12 w-56 h-56 rounded-full bg-accent-100/8 blur-[100px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-5 md:px-8 lg:px-12 flex flex-col py-12 md:py-16 lg:py-20 gap-6 md:gap-8">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center flex-shrink-0 mb-2 md:mb-3">
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-accent-50/70 border border-accent-200/30 text-[10px] font-label font-semibold text-accent-600 uppercase tracking-[0.1em] mb-1 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot w-1.5 h-1.5" />
            传统听评课
          </span>

          <h2 className={`font-heading text-xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-1 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            专家经验是<span className="text-primary-500">AI评课的专业起点</span>
          </h2>

          <p className={`text-xs text-foreground-500 font-body max-w-xl mx-auto leading-snug transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            传统听评课非常有价值，而且这个价值是AI评课的基础。AI不是替代人工经验，而是把人工经验<strong className="text-foreground-700">模型化、规模化、证据化</strong>。
          </p>
        </div>

        {/* 场景条 */}
        <div className={`flex-shrink-0 mb-2 md:mb-3 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-2 md:gap-3 px-3 py-1.5 md:px-4 md:py-2 rounded-xl glass-blue border border-primary-200/25">
            <div className="w-6 h-6 md:w-7 md:h-7 rounded-md bg-primary-50 border border-primary-200/30 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-voice-line text-xs text-primary-600" />
            </div>
            <div className="flex-shrink-0">
              <h4 className="font-heading text-xs font-bold text-foreground-700">传统听评课场景</h4>
            </div>
            <div className="w-px h-4 bg-primary-200/30 hidden md:block" />
            <div className="flex gap-1.5 flex-wrap">
              {sceneTags.map((tag) => (
                <span key={tag} className="px-1.5 py-0.5 rounded-full bg-white/60 border border-primary-200/25 text-[10px] font-body text-primary-600 whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <div className="hidden lg:flex items-center gap-1 ml-auto flex-shrink-0">
              <div className="w-1.5 h-1.5 rounded-full bg-accent-400/60 animate-pulse" />
              <span className="text-[10px] font-body text-foreground-400">经验沉淀中</span>
            </div>
          </div>
        </div>

        {/* 五大价值 */}
        <div className="flex-shrink-0">
          <div className="hidden lg:grid grid-cols-5 gap-3">
            {fiveValues.map((val, i) => (
              <ValueCard key={val.keyword} item={val} index={i} titleVisible={titleVisible} isFeatured={i === 0} isAccent={i === 4} />
            ))}
          </div>

          <div className="hidden md:grid lg:hidden grid-cols-3 gap-3">
            {fiveValues.map((val, i) => (
              <ValueCard key={val.keyword} item={val} index={i} titleVisible={titleVisible} isFeatured={i === 0} isAccent={i === 4} />
            ))}
          </div>

          <div className="md:hidden flex flex-col gap-2">
            {fiveValues.map((val, i) => (
              <ValueCard key={val.keyword} item={val} index={i} titleVisible={titleVisible} isFeatured={i === 0} isAccent={i === 4} />
            ))}
          </div>
        </div>

        {/* 底部总结 */}
        <div className={`flex-shrink-0 flex justify-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl glass border border-background-200/40">
            <div className="w-5 h-5 rounded-md bg-primary-50 border border-primary-200/30 flex items-center justify-center flex-shrink-0">
              <i className="ri-link text-[10px] text-primary-500" />
            </div>
            <span className="text-xs font-body text-foreground-500">
              AI评课的专业起点，正是这些年人工听评课沉淀下来的<strong className="text-foreground-700">标准、经验、样本和判断</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* 价值卡片子组件 */
function ValueCard({
  item,
  index,
  titleVisible,
  isFeatured = false,
  isAccent = false,
}: {
  item: typeof fiveValues[0];
  index: number;
  titleVisible: boolean;
  isFeatured?: boolean;
  isAccent?: boolean;
}) {
  const isGold = isFeatured || isAccent;

  return (
    <div
      className={`group transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
      style={{ transitionDelay: `${400 + index * 100}ms` }}
    >
      <div
        className={`relative h-full rounded-lg overflow-hidden p-2.5 md:p-3 border transition-all duration-400 hover:-translate-y-0.5 ${
          isGold
            ? 'glass-liquid-gold border-accent-200/25 hover:border-accent-300/40'
            : 'glass-liquid border-primary-200/25 hover:border-primary-300/40'
        }`}
      >
        {/* 顶部渐变横条 */}
        <div
          className={`absolute top-0 left-2 right-2 h-[2px] rounded-full ${
            isGold
              ? 'bg-gradient-to-r from-transparent via-accent-400/70 to-transparent'
              : 'bg-gradient-to-r from-transparent via-primary-400/70 to-transparent'
          }`}
        />

        {/* 图标 */}
        <div
          className={`w-7 h-7 md:w-8 md:h-8 rounded-lg flex items-center justify-center mb-1.5 transition-transform duration-300 group-hover:scale-105 ${
            isGold
              ? 'bg-accent-100/60 border border-accent-300/30'
              : 'bg-primary-50 border border-primary-200/30'
          }`}
        >
          <i className={`${item.icon} text-sm md:text-base ${isGold ? 'text-accent-600' : 'text-primary-600'}`} />
        </div>

        {/* 内容 */}
        <div className="mb-0.5">
          <div className="flex items-center gap-1 mb-0.5">
            <h4 className="font-heading text-xs font-bold text-foreground-800">
              {item.keyword}
            </h4>
            {isFeatured && (
              <i className="ri-star-fill text-[8px] text-accent-500" />
            )}
            <span className={`text-[9px] font-label font-bold px-1 py-0.5 rounded-full ml-auto ${isGold ? 'bg-accent-100 text-accent-700' : 'bg-primary-50 text-primary-700'}`}>
              {String(index + 1).padStart(2, '0')}
            </span>
          </div>
          <p className="text-[10px] md:text-[11px] text-foreground-500 font-body leading-snug">{item.desc}</p>
        </div>

        {/* hover 底部装饰 */}
        <div className={`absolute bottom-1.5 right-1.5 w-1 h-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${isGold ? 'bg-accent-400/40' : 'bg-primary-400/40'}`} />
      </div>
    </div>
  );
}